import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useAccessToken } from '../useAccessToken'
import { useCallback } from 'react'
import useProjectId from '../../customHooks/useProjectId'
import { ProjectAttributesDto } from '../../interfaces/editing/dtos/ProjectAttributesDto'
import { useSaveSheets } from './useSaveSheets'
import { useEditingContext } from '../../context/EditingContext'
import { createEditsDto } from '../../utils/createEditsDto'
import { FetchResponse, makeResultFetch } from '../genericFetch'
import { apiRequestParams } from '../fetchOptions'
import { useHandleError } from '../useHandleError'

export const useSaveProjectAttributes = () => {
  const getAccessToken = useAccessToken()
  const handleError = useHandleError()
  const projectId = useProjectId()
  const saveSheets = useSaveSheets()
  const { edits, setEdits } = useEditingContext()
  const queryClient = useQueryClient()

  const mutationFunction = useCallback(async () => {
    return getAccessToken().then((accessToken) => {
      if (!accessToken) throw new Error('Access token is undefined')

      const editsDto = createEditsDto(edits)

      const fetchedData = makeResultFetch<boolean>(
        `/Projects/${projectId}/Save`,
        apiRequestParams<ProjectAttributesDto>(
          accessToken,
          'PUT',
          editsDto.projectAttributes
        )
      ).then((response: FetchResponse<boolean>) => {
        const { message, success } = response

        if (!success) throw Error(message)

        return response
      })

      return fetchedData
    })
  }, [edits, getAccessToken, projectId])

  const handleSuccess = (response: FetchResponse<boolean>) => {
    if (response.success) {
      setEdits((prevState) => ({ ...prevState, projectAttributes: {} }))

      if (
        Object.keys(edits.projectAttributes)?.length > 0 ||
        Object.keys(edits.comments)?.length > 0
      ) {
        queryClient.invalidateQueries({ queryKey: ['projectInfo', projectId] })
        queryClient.invalidateQueries({
          queryKey: ['baseCalculations', projectId],
        })

        saveSheets.mutate()
      }
    }
  }

  const saveProjectAttributes = useMutation({
    mutationFn: mutationFunction,
    onSuccess: handleSuccess,
    onError: handleError,
  })

  return { saveProjectAttributes, sheetsMutation: saveSheets }
}
